import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import anime from 'animejs';

import { Link } from '../Link';
import { Text } from '../Text';
import { IconGithub, IconExternalLink } from '../Icons';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_LABELS = [
  { index: 1, label: 'Mon' },
  { index: 3, label: 'Wed' },
  { index: 5, label: 'Fri' }
];

const COLOR_LEVELS = [
  { fill: '#121214', stroke: 'rgba(255, 255, 255, 0.08)', strokeWidth: 1 },
  { fill: '#27272a', stroke: 'rgba(255, 255, 255, 0.2)', strokeWidth: 1 },
  { fill: '#52525b', stroke: 'rgba(255, 255, 255, 0.4)', strokeWidth: 1 },
  { fill: '#a1a1aa', stroke: 'rgba(255, 255, 255, 0.65)', strokeWidth: 1 },
  { fill: '#f4f4f5', stroke: '#ffffff', strokeWidth: 1 }
];

function getLevelColor (count, level) {
  if (count === 0 || level === 0) return COLOR_LEVELS[0];
  if (count >= 25 || level >= 4) return COLOR_LEVELS[4];
  if (count >= 12 || level >= 3) return COLOR_LEVELS[3];
  if (count >= 5 || level >= 2) return COLOR_LEVELS[2];
  return COLOR_LEVELS[1];
}

class Component extends React.PureComponent {
  static displayName = 'GithubContributions';

  static propTypes = {
    classes: PropTypes.object.isRequired,
    energy: PropTypes.object.isRequired,
    audio: PropTypes.object.isRequired,
    sounds: PropTypes.object.isRequired,
    className: PropTypes.string,
    username: PropTypes.string,
    onEnter: PropTypes.func,
    onExit: PropTypes.func
  };

  static defaultProps = {
    username: 'melihemik'
  };

  constructor (props) {
    super(props);

    this.state = {
      loading: true,
      total: 5338,
      weeks: [],
      monthLabels: []
    };
  }

  componentDidMount () {
    this.fetchContributions();
  }

  componentWillUnmount () {
    if (this.element) {
      anime.remove(this.element);
      const cells = this.element.querySelectorAll('rect');
      anime.remove(cells);
    }
  }

  enter () {
    const { energy, onEnter } = this.props;
    const duration = energy && energy.duration ? energy.duration.enter : 500;

    anime({
      targets: this.element,
      opacity: [0, 1],
      translateY: [14, 0],
      easing: 'easeOutCubic',
      duration,
      complete: () => {
        const cells = this.element ? this.element.querySelectorAll('rect') : [];
        if (cells.length) {
          anime({
            targets: cells,
            opacity: [0, 1],
            scale: [0.7, 1],
            easing: 'easeOutCubic',
            delay: anime.stagger(1.2, { start: 50 }),
            duration: 350
          });
        }
        onEnter && onEnter();
      }
    });
  }

  exit () {
    const { energy, onExit } = this.props;
    const duration = energy && energy.duration ? energy.duration.exit : 300;

    anime({
      targets: this.element,
      opacity: [1, 0],
      translateY: [0, -10],
      easing: 'easeInCubic',
      duration,
      complete: () => onExit && onExit()
    });
  }

  fetchContributions = async () => {
    const { username } = this.props;

    try {
      const response = await fetch(`https://github-contributions-api.jogruber.de/v4/${username}?y=last`);
      if (!response.ok) {
        throw new Error('Primary API error');
      }
      const data = await response.json();
      this.processData(data);
    } catch (err) {
      try {
        const fallbackRes = await fetch(`https://github-contributions.vercel.app/api/v1/${username}`);
        if (!fallbackRes.ok) {
          throw new Error('Fallback API error');
        }
        const fallbackData = await fallbackRes.json();
        const contributions = fallbackData.contributions || [];
        const total = contributions.reduce((sum, item) => sum + (item.count || 0), 0);
        this.processData({ total: { lastYear: total }, contributions });
      } catch (fallbackErr) {
        this.setState({ loading: false });
      }
    }
  }

  processData = (data) => {
    const contributions = data.contributions || [];
    const total = (data.total && data.total.lastYear) || contributions.reduce((sum, d) => sum + (d.count || 0), 0);

    if (!contributions.length) {
      this.setState({ loading: false, total });
      return;
    }

    const weeks = [];
    let currentWeek = [];

    const firstDate = new Date(contributions[0].date);
    const firstDayOfWeek = firstDate.getDay();

    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(null);
    }

    contributions.forEach(day => {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    });

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    const monthLabels = [];
    let lastMonth = -1;

    weeks.forEach((week, weekIndex) => {
      const firstValidDay = week.find(d => d !== null);
      if (firstValidDay) {
        const month = new Date(firstValidDay.date).getMonth();
        const lastLabelIndex = monthLabels.length ? monthLabels[monthLabels.length - 1].weekIndex : -4;
        if (month !== lastMonth && (weekIndex - lastLabelIndex) >= 2) {
          monthLabels.push({
            weekIndex,
            label: MONTH_NAMES[month]
          });
          lastMonth = month;
        }
      }
    });

    this.setState({
      loading: false,
      total,
      weeks,
      monthLabels
    });
  }

  onCellHover = () => {
    const { sounds } = this.props;
    if (sounds && sounds.hover) {
      sounds.hover.play();
    }
  }

  renderGrid () {
    const { classes } = this.props;
    const { weeks, monthLabels } = this.state;

    const cellSize = 10.5;
    const cellGap = 3.2;
    const step = cellSize + cellGap;
    const leftMargin = 30;
    const topMargin = 20;

    const totalWidth = leftMargin + (weeks.length * step) + 10;
    const totalHeight = topMargin + (7 * step) + 8;

    return (
      <svg
        className={classes.svg}
        viewBox={`0 0 ${totalWidth} ${totalHeight}`}
        preserveAspectRatio='xMinYMin meet'
        aria-label='GitHub Contribution Heatmap'
        role='img'
      >
        {monthLabels.map(m => (
          <text
            key={m.weekIndex + m.label}
            x={leftMargin + (m.weekIndex * step)}
            y={topMargin - 7}
            className={classes.monthText}
          >
            {m.label}
          </text>
        ))}

        {DAY_LABELS.map(d => (
          <text
            key={d.label}
            x={leftMargin - 7}
            y={topMargin + (d.index * step) + 8.5}
            textAnchor='end'
            className={classes.dayText}
          >
            {d.label}
          </text>
        ))}

        {weeks.map((week, weekIndex) => (
          <g key={weekIndex} transform={`translate(${leftMargin + (weekIndex * step)}, ${topMargin})`}>
            {week.map((day, dayIndex) => {
              if (!day) return null;
              const colorInfo = getLevelColor(day.count, day.level);

              return (
                <rect
                  key={day.date}
                  className={classes.cell}
                  x={0}
                  y={dayIndex * step}
                  width={cellSize}
                  height={cellSize}
                  fill={colorInfo.fill}
                  stroke={colorInfo.stroke}
                  strokeWidth={colorInfo.strokeWidth}
                  onMouseEnter={this.onCellHover}
                >
                  <title>
                    {`${day.count} contribution${day.count === 1 ? '' : 's'} on ${day.date}`}
                  </title>
                </rect>
              );
            })}
          </g>
        ))}
      </svg>
    );
  }

  render () {
    const { classes, className, username, sounds, energy, audio } = this.props;
    const { loading, total } = this.state;
    const formattedTotal = Number(total).toLocaleString();

    return (
      <div
        className={cx(classes.root, className)}
        ref={ref => (this.element = ref)}
      >
        <div className={classes.header}>
          <a
            href={`https://github.com/${username}`}
            target='_blank'
            rel='noreferrer'
            className={classes.userBadge}
            onMouseEnter={() => sounds && sounds.hover && sounds.hover.play()}
          >
            <IconGithub className={classes.icon} size='1em' />
            <span className={classes.userText}>
              <Text energy={energy} audio={audio}>
                {`@${username}`}
              </Text>
            </span>
          </a>

          <div className={classes.statsBadge}>
            <span className={classes.statsNumber}>
              <Text energy={energy} audio={audio}>
                {formattedTotal}
              </Text>
            </span>
            <span className={classes.statsLabel}>
              <Text energy={energy} audio={audio}>
                contributions in the last year
              </Text>
            </span>
          </div>
        </div>

        <div className={classes.scrollContainer}>
          {loading ? (
            <div className={classes.loading}>
              <Text energy={energy} audio={audio}>
                [ SYNCHRONIZING GITHUB DATA... ]
              </Text>
            </div>
          ) : (
            this.renderGrid()
          )}
        </div>

        <div className={classes.footer}>
          <div className={classes.legend}>
            <span className={classes.legendLabelLess}>
              <Text energy={energy} audio={audio}>
                Less
              </Text>
            </span>
            {COLOR_LEVELS.map((col, idx) => (
              <span
                key={idx}
                className={classes.legendBox}
                style={{
                  backgroundColor: col.fill,
                  border: `1px solid ${col.stroke}`
                }}
              />
            ))}
            <span className={classes.legendLabelMore}>
              <Text energy={energy} audio={audio}>
                More
              </Text>
            </span>
          </div>

          <Link
            className={classes.profileLink}
            href={`https://github.com/${username}`}
            target='github'
            rel='noreferrer'
            onMouseEnter={() => sounds && sounds.hover && sounds.hover.play()}
          >
            <Text energy={energy} audio={audio}>
              View full GitHub profile
            </Text>
            <IconExternalLink size='1.1em' />
          </Link>
        </div>
      </div>
    );
  }
}

export { Component };
